import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { login, smsLogin, bindAccount, createAccount, sendSmsCode } from '../../api.js';
import { useUser } from '../../store/UserContext';
import AlertModal from '../../components/AlertModal';
import { setUserAndToken } from '../../utils/token.js';
import { setCachedPermissions } from '../../utils/permissions.js';
import { fetchAuthContext } from '../../api/auth.js';
import { navigateAfterLogin } from '../../utils/postLoginNavigation.js';
import { t } from '../../i18n';
import { APP_LOGO, APP_LOGO_ALT } from '../../config/brand';

function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setUser } = useUser();

  // 登录方式：password, sms
  const [loginType, setLoginType] = useState('password');
  const [sessionNotice, setSessionNotice] = useState('');

  // 密码登录表单
  const [passwordForm, setPasswordForm] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const passwordSubmittingRef = useRef(false);

  // 短信登录表单
  const [smsForm, setSmsForm] = useState({ phone: '', code: '' });
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const smsSubmittingRef = useRef(false);

  // 第三方登录状态
  const [thirdPartyLoading, setThirdPartyLoading] = useState(false);

  // 绑定第三方账号状态
  const [bindData, setBindData] = useState(null);
  const [bindForm, setBindForm] = useState({ username: '', password: '' });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // AlertModal 状态
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', title: t('login_alert_title'), type: 'info' });

  const showAlert = (message, title = t('login_alert_title'), type = 'info') => {
    setAlertModal({ isOpen: true, message, title, type });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, message: '', title: t('login_alert_title'), type: 'info' });
  };

  // 登录安全设置状态
  const [securitySettings, setSecuritySettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // 会话超时/失效跳转回来时提示重新登录
  useEffect(() => {
    if (searchParams.get('reason') !== 'relogin') return;
    setSessionNotice(t('login_session_expired'));
    const next = new URLSearchParams(searchParams);
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // 获取登录安全设置
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        const response = await fetch('/api/login-security-settings', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setSecuritySettings(data.data);
          }
        }
      } catch (err) {
        console.error('获取登录安全设置失败:', err);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSecuritySettings();
  }, []);

  // 密码登录处理
  const handlePasswordChange = e => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async e => {
    e.preventDefault();
    if (passwordSubmittingRef.current || passwordLoading) return;
    setError('');
    if (!passwordForm.username || !passwordForm.password) {
      showAlert(t('login_error_required_credentials'), t('login_alert_title'), 'warning');
      return;
    }
    passwordSubmittingRef.current = true;
    setPasswordLoading(true);
    try {
      const res = await login({ ...passwordForm, rememberMe });

      if (!res || !res.data) {
        throw new Error('登录响应为空');
      }

      if (!res.data.user || !res.data.user.username) {
        throw new Error('登录响应数据不完整');
      }

      const userData = res.data.user;
      const accessToken = res.data.accessToken || res.data.token;
      if (!accessToken) {
        throw new Error('登录未返回有效令牌');
      }
      const refreshToken = res.data.refreshToken;
      const expiresIn = res.data.expiresIn || res.data.expiresInSec || 3600;

      if (setUserAndToken(userData, accessToken, refreshToken, expiresIn)) {
        if (Array.isArray(res.data.permissions)) {
          setCachedPermissions(res.data.permissions);
        }
        setUser(userData);
      } else {
        throw new Error('登录状态保存失败');
      }

      setError('');
      await navigateAfterLogin(navigate);
    } catch (err) {
      if (err.response) {
        const errorData = err.response.data;
        const errorMessage = errorData?.message || errorData?.error || err.message || t('login_error_login_failed');
        showAlert(errorMessage, t('login_alert_title'), 'error');
      } else if (err.request) {
        const isTimeout =
          err.code === 'ECONNABORTED' ||
          (typeof err.message === 'string' && err.message.toLowerCase().includes('timeout'));
        showAlert(
          isTimeout
            ? '连接后端超时：请确认 Spring Boot 已完全启动（首次启动可能需数分钟），MySQL 已运行，且开发代理指向正确端口（默认后端 8088）。'
            : '网络请求失败，请检查后端服务是否启动',
          t('login_alert_title'),
          'error'
        );
      } else {
        showAlert(err.message || t('login_error_login_failed'), t('login_alert_title'), 'error');
      }
    } finally {
      passwordSubmittingRef.current = false;
      setPasswordLoading(false);
    }
  };

  // 短信登录处理
  const handleSmsChange = e => {
    setSmsForm({ ...smsForm, [e.target.name]: e.target.value });
  };

  const handleSendSmsCode = async () => {
    if (!smsForm.phone) {
      showAlert(t('login_error_enter_phone'), t('login_alert_title'), 'warning');
      return;
    }

    setSmsLoading(true);
    setError('');
    try {
      const response = await sendSmsCode(smsForm.phone, smsForm.tenantCode || passwordForm.tenantCode || '');

      if (response.data.success) {
        showAlert(
          response.data.message || t('login_sms_send_success_message'),
          t('login_sms_send_success_title'),
          'success'
        );

        setSmsCountdown(60);
        const timer = setInterval(() => {
          setSmsCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        showAlert(response.data.error || t('login_sms_send_failed_message'), t('login_sms_send_failed_title'), 'error');
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_sms_send_failed_message'), t('login_sms_send_failed_title'), 'error');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSmsSubmit = async e => {
    e.preventDefault();
    if (smsSubmittingRef.current || smsSubmitting) return;
    setError('');
    smsSubmittingRef.current = true;
    setSmsSubmitting(true);
    try {
      const res = await smsLogin(smsForm);

      if (!res.data.user || !res.data.user.username) {
        throw new Error('登录响应数据不完整');
      }

      const userData = res.data.user;
      const accessToken = res.data.accessToken || res.data.token;
      if (!accessToken) {
        throw new Error('登录未返回有效令牌');
      }
      const refreshToken = res.data.refreshToken;
      const expiresIn = res.data.expiresIn || res.data.expiresInSec || 3600;

      if (setUserAndToken(userData, accessToken, refreshToken, expiresIn)) {
        if (Array.isArray(res.data.permissions)) {
          setCachedPermissions(res.data.permissions);
        }
        setUser(userData);
        setError('');
        await navigateAfterLogin(navigate);
      } else {
        throw new Error('登录状态保存失败');
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_login_failed'), t('login_alert_title'), 'error');
    } finally {
      smsSubmittingRef.current = false;
      setSmsSubmitting(false);
    }
  };

  // 第三方登录处理
  const handleWechatLogin = async () => {
    setThirdPartyLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users/wechat-qrcode', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      if (res.ok && data.qrCodeUrl) {
        await showWechatQrCodeModal(data.qrCodeUrl, data.ticket);
      } else {
        throw new Error(data.message || '获取微信二维码失败');
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_wechat_failed'), t('login_alert_title'), 'error');
    } finally {
      setThirdPartyLoading(false);
    }
  };

  const generateWechatQRCode = async (qrCodeUrl, modal) => {
    const QRCode = await import('qrcode');
    const qrCodeDataUrl = await QRCode.default.toDataURL(qrCodeUrl, {
      width: 200,
      height: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });

    const container = modal.querySelector('#wechat-qr-container');
    container.innerHTML = `<img src="${qrCodeDataUrl}" alt="${t('login_wechat_qr_alt')}" class="w-full h-full object-contain" />`;
  };

  const showWechatQrCodeModal = async (qrCodeUrl, ticket) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/40 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-md">
        <div class="text-center">
          <h3 class="text-lg font-semibold mb-4 text-gray-900">${t('login_wechat_modal_title')}</h3>
          <div class="mb-4">
            <div id="wechat-qr-container" class="mx-auto border border-gray-200 w-48 h-48 flex items-center justify-center bg-gray-50">
              <div class="text-center text-gray-500">
                <div class="text-sm">${t('login_wechat_modal_generating')}</div>
              </div>
            </div>
          </div>
          <p class="text-sm text-gray-600 mb-4">${t('login_wechat_modal_instruction')}</p>
          <button type="button" data-close-wechat-modal
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            ${t('login_cancel')}
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('[data-close-wechat-modal]')?.addEventListener('click', () => modal.remove());

    try {
      await generateWechatQRCode(qrCodeUrl, modal);
    } catch (error) {
      console.error('生成微信二维码失败:', error);
      const container = modal.querySelector('#wechat-qr-container');
      container.innerHTML = `
        <div class="text-center text-red-500">
          <div class="text-sm">${t('login_wechat_modal_failed')}</div>
          <div class="text-xs mt-1">${t('login_wechat_modal_retry')}</div>
        </div>
      `;
    }

    startWechatQrCodePolling(ticket, modal);
  };

  const startWechatQrCodePolling = (ticket, modal) => {
    let pollCount = 0;
    const maxPolls = 60;

    const poll = async () => {
      if (pollCount >= maxPolls) {
        modal.remove();
        showAlert(t('login_error_qrcode_expired'), t('login_alert_title'), 'warning');
        return;
      }

      try {
        const res = await fetch(`/api/users/wechat-qrcode-status/${ticket}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await res.json();
        if (res.ok && data.scanned) {
          modal.remove();
          await handleWechatAuthCode(data.code);
          return;
        }

        pollCount++;
        setTimeout(poll, 3000);
      } catch (error) {
        console.error('轮询错误:', error);
        pollCount++;
        setTimeout(poll, 3000);
      }
    };

    poll();
  };

  const handleWechatAuthCode = async (code) => {
    try {
      const res = await fetch('/api/users/wechat-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });

      const data = await res.json();
      if (res.ok) {
        if (data.needBind) {
          setBindData(data);
        } else if (data.success && data.user) {
          const userData = data.user;
          const accessToken = data.accessToken || data.token;
          const refreshToken = data.refreshToken;
          const expiresIn = data.expiresIn || 3600;
          setUserAndToken(userData, accessToken, refreshToken, expiresIn);
          setUser(userData);
          await navigateAfterLogin(navigate);
        } else {
          throw new Error(data.message || '登录失败');
        }
      } else {
        throw new Error(data.message || '登录失败');
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_wechat_failed'), t('login_alert_title'), 'error');
    }
  };

  const handleAlipayLogin = async () => {
    setThirdPartyLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users/alipay-qrcode', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();
      if (res.ok && data.qrCodeUrl) {
        showAlipayQrCodeModal(data.qrCodeUrl, data.state);
      } else {
        throw new Error(data.message || '获取支付宝二维码失败');
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_alipay_failed'), t('login_alert_title'), 'error');
    } finally {
      setThirdPartyLoading(false);
    }
  };

  const showAlipayQrCodeModal = (qrCodeUrl, state) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/40 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-md">
        <div class="text-center">
          <h3 class="text-lg font-semibold mb-4 text-gray-900">${t('login_alipay_modal_title')}</h3>
          <div class="mb-4">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}"
                 alt="${t('login_alipay_qr_alt')}" class="mx-auto border border-gray-200" />
          </div>
          <p class="text-sm text-gray-600 mb-4">${t('login_alipay_modal_instruction')}</p>
          <button type="button" data-close-alipay-modal
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            ${t('login_cancel')}
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('[data-close-alipay-modal]')?.addEventListener('click', () => modal.remove());
    startAlipayQrCodePolling(state, modal);
  };

  const startAlipayQrCodePolling = (state, modal) => {
    let pollCount = 0;
    const maxPolls = 60;

    const poll = async () => {
      if (pollCount >= maxPolls) {
        modal.remove();
        showAlert(t('login_error_timeout'), t('login_alert_title'), 'warning');
        return;
      }

      pollCount++;

      try {
        const user = localStorage.getItem('user');
        if (user) {
          modal.remove();
          return;
        }
        setTimeout(poll, 5000);
      } catch (err) {
        console.error('支付宝登录轮询失败:', err);
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  // 绑定账号处理
  const handleBindChange = e => {
    setBindForm({ ...bindForm, [e.target.name]: e.target.value });
  };

  const handleBindSubmit = async e => {
    e.preventDefault();
    setError('');
    try {
      const loginRes = await login(bindForm);

      const bindRes = await bindAccount({
        userId: loginRes.data.user.id,
        platform: bindData.platform,
        platformUserId: bindData.platformUserId,
        nickname: bindData.nickname,
        avatar: bindData.avatar
      });

      if (bindRes.data.success) {
        const userData = bindRes.data.user;
        const accessToken = loginRes.data.accessToken || loginRes.data.token;
        const refreshToken = loginRes.data.refreshToken;
        const expiresIn = loginRes.data.expiresIn || 3600;
        setUserAndToken(userData, accessToken, refreshToken, expiresIn);
        setUser(userData);
        setBindData(null);
        await navigateAfterLogin(navigate);
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_bind_failed'), t('login_alert_title'), 'error');
    }
  };

  const handleCreateAndBind = async () => {
    setError('');
    try {
      const res = await createAccount({
        platform: bindData.platform,
        platformUserId: bindData.platformUserId,
        nickname: bindData.nickname,
        avatar: bindData.avatar
      });

      if (res.data.success) {
        const userData = res.data.user;
        const accessToken = res.data.accessToken || res.data.token;
        if (!accessToken) {
          throw new Error('注册登录未返回有效令牌');
        }
        setUserAndToken(userData, accessToken, res.data.refreshToken || null, res.data.expiresIn || 3600);
        setUser(userData);
        setBindData(null);
        await navigateAfterLogin(navigate);
      }
    } catch (err) {
      showAlert(err.response?.data?.error || err.message || t('login_error_create_failed'), t('login_alert_title'), 'error');
    }
  };

  const inputClass =
    'flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600';
  const primaryBtnClass =
    'w-full bg-green-700 text-white py-2.5 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors';
  const tabClass = (active) =>
    `flex-1 py-2 px-2 sm:px-4 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-green-700 text-green-800'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  // 绑定账号弹窗
  if (bindData) {
    return (
      <div className="h-screen overflow-hidden min-w-[320px] flex items-center justify-center bg-stone-50 px-0 sm:px-4">
        <div className="w-full max-w-sm mx-auto">
          <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-sm p-5 sm:p-6 border-0 sm:border sm:border-gray-200">
            <div className="text-center mb-4">
              <h2 className="text-lg font-bold text-gray-900 mb-1">{t('login_bind_title')}</h2>
              <p className="text-sm text-gray-600">{t(bindData.platform === 'WECHAT' ? 'login_bind_subtitle_wechat' : 'login_bind_subtitle_alipay')}</p>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-center mb-3">
                <img src={bindData.avatar} alt="头像" className="w-12 h-12 rounded-full mr-3" />
                <div>
                  <p className="font-medium text-sm">{bindData.nickname}</p>
                  <p className="text-xs text-gray-500">{t(bindData.platform === 'WECHAT' ? 'login_bind_wechat_user' : 'login_bind_alipay_user')}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCreateAndBind}
                className={primaryBtnClass}
              >
                {t('login_create_and_bind')}
              </button>

              <div className="text-center text-sm text-gray-500">{t('login_or')}</div>

              <form onSubmit={handleBindSubmit} className="space-y-3">
                <div>
                  <input
                    name="username"
                    value={bindForm.username}
                    onChange={handleBindChange}
                    placeholder={t('login_username_placeholder')}
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <input
                    name="password"
                    type="password"
                    value={bindForm.password}
                    onChange={handleBindChange}
                    placeholder={t('login_password_placeholder')}
                    className={inputClass}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-900 transition-colors"
                >
                  {t('login_bind_existing')}
                </button>
              </form>
            </div>

            {error && (
              <div className="mt-3 text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-2">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden min-w-[320px] flex items-center justify-center bg-stone-50 px-0 sm:px-4">
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-sm px-5 py-5 sm:px-6 sm:py-6 border-0 sm:border sm:border-gray-200 relative">
          <div className="text-center mb-4">
            <div className="flex flex-col items-center justify-center mb-1.5">
              <img
                src={APP_LOGO}
                alt={APP_LOGO_ALT}
                className="h-10 sm:h-12 w-auto object-contain mb-1.5"
              />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">{t('systemName')}</h1>
            </div>
            <p className="text-gray-500 text-xs">{t('login_subtitle')}</p>
          </div>

          {sessionNotice && (
            <div className="mb-3 text-amber-800 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-center">
              {sessionNotice}
            </div>
          )}

          {/* 登录方式标签页 */}
          <div className="mb-3">
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                onClick={() => setLoginType('password')}
                className={tabClass(loginType === 'password')}
              >
                {t('login_password_tab')}
              </button>
              <button
                type="button"
                onClick={() => setLoginType('sms')}
                className={tabClass(loginType === 'sms')}
              >
                {t('login_sms_tab')}
              </button>
            </div>
          </div>

          {/* 密码登录表单 */}
          {loginType === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                <label htmlFor="username" className="text-sm font-medium text-gray-700 sm:min-w-[52px]">
                  {t('login_username_label')}
                </label>
                <input
                  id="username"
                  name="username"
                  value={passwordForm.username}
                  onChange={handlePasswordChange}
                  placeholder={t('login_username_placeholder')}
                  className={inputClass}
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                <label htmlFor="password" className="text-sm font-medium text-gray-700 sm:min-w-[52px]">
                  {t('login_password_label')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={passwordForm.password}
                  onChange={handlePasswordChange}
                  placeholder={t('login_password_placeholder')}
                  className={inputClass}
                  required
                />
              </div>

              {!loadingSettings && securitySettings && securitySettings.enableRememberMe && (
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-green-700 focus:ring-green-600 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    {t('login_remember_me')}
                  </label>
                </div>
              )}

              {error && (
                <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-md p-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className={primaryBtnClass}
              >
                {passwordLoading ? t('login_submitting') : t('login_submit')}
              </button>
            </form>
          )}

          {/* 短信登录表单 */}
          {loginType === 'sms' && (
            <form onSubmit={handleSmsSubmit} className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                <label htmlFor="phone" className="text-sm font-medium text-gray-700 sm:min-w-[52px]">
                  {t('login_phone_label')}
                </label>
                <input
                  id="phone"
                  name="phone"
                  value={smsForm.phone}
                  onChange={handleSmsChange}
                  placeholder={t('login_phone_placeholder')}
                  className={inputClass}
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                <label htmlFor="code" className="text-sm font-medium text-gray-700 sm:min-w-[52px]">
                  {t('login_code_label')}
                </label>
                <div className="flex space-x-2 flex-1">
                  <input
                    id="code"
                    name="code"
                    value={smsForm.code}
                    onChange={handleSmsChange}
                    placeholder={t('login_code_placeholder')}
                    className={inputClass}
                    required
                  />
                  <button
                    type="button"
                    onClick={handleSendSmsCode}
                    disabled={smsCountdown > 0 || smsLoading}
                    className="px-3 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
                  >
                    {smsCountdown > 0 ? `${smsCountdown}s` : smsLoading ? t('login_sending_code') : t('login_send_code')}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-md p-2">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-green-700 text-xs bg-green-50 border border-green-200 rounded-md p-2">
                  {success}
                </div>
              )}

              <button
                type="submit"
                disabled={smsSubmitting}
                className={primaryBtnClass}
              >
                {smsSubmitting ? t('login_submitting') : t('login_submit')}
              </button>
            </form>
          )}

          {/* 第三方登录 */}
          <div className="mt-4">
            <div className="relative mb-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">其他方式</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleWechatLogin}
                disabled={thirdPartyLoading}
                className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 4.882-1.932 7.621-.568-.302-3.706-3.78-6.6-8.1-6.6z" />
                </svg>
                {t('login_wechat')}
              </button>
              <button
                type="button"
                onClick={handleAlipayLogin}
                disabled={thirdPartyLoading}
                className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21.422 15.358c-1.037-.803-2.38-1.478-3.476-2.018-1.097-.54-2.777-1.037-4.132-1.037-1.355 0-3.035.497-4.132 1.037-1.096.54-2.439 1.215-3.476 2.018C4.5 16.358 3 18.358 3 20.358c0 2 1.5 4 4.422 5.358 1.037.803 2.38 1.478 3.476 2.018 1.097.54 2.777 1.037 4.132 1.037 1.355 0 3.035-.497 4.132-1.037 1.096-.54 2.439-1.215 3.476-2.018C19.5 24.358 21 22.358 21 20.358c0-2-1.5-4-4.422-5.358z" />
                </svg>
                {t('login_alipay')}
              </button>
            </div>
          </div>

          <div className="mt-4 text-center">
            <div className="flex justify-center items-center space-x-3">
              <Link
                to="/register"
                className="text-green-700 hover:text-green-900 hover:underline text-xs transition-colors"
              >
                {t('login_register')}
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to="/reset-password"
                className="text-gray-600 hover:text-gray-800 hover:underline text-xs transition-colors"
              >
                {t('login_forgot_password')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        title={alertModal.title}
        type={alertModal.type}
        onClose={closeAlert}
      />
    </div>
  );
}

export default LoginPage;
